import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CompanyLogo } from './CompanyLogo';
import { tokens } from '../../../design';

/**
 * «Так шапка выглядит в отчёте» (AUT-403) — the company brand block of a
 * report, after the mockup preview: logo / initials, name, «подбор авто ·
 * город», the phone, and one chip per contact that is set (no contact →
 * no button; a member's personal phone is never substituted). Purely
 * visual — CP-06 renders the real report header.
 */
export interface BrandPreviewProps {
  name: string;
  city: string;
  logoUrl: string | null;
  phone: string;
  telegram: string;
  site: string;
  memberName?: string;
}

export const BrandPreview = ({
  name,
  city,
  logoUrl,
  phone,
  telegram,
  site,
  memberName,
}: BrandPreviewProps): ReactElement => {
  const { t } = useTranslation();
  const title = name.trim() || t('companies.preview.namePlaceholder');
  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: tokens.surface.elevated }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CompanyLogo name={title} logoUrl={logoUrl} size={40} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {city.trim()
              ? t('companies.preview.tagline', { city: city.trim() })
              : t('companies.preview.taglineNoCity')}
          </Typography>
        </Box>
      </Box>
      {phone.trim() ? (
        <Typography variant="body2" fontFamily="monospace" sx={{ mt: 1.5 }}>
          {phone.trim()}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
        {phone.trim() ? (
          <Chip size="small" color="primary" label={t('companies.preview.call')} />
        ) : null}
        {telegram.trim() ? (
          <Chip size="small" variant="outlined" label={t('companies.preview.telegram')} />
        ) : null}
        {site.trim() ? (
          <Chip size="small" variant="outlined" label={t('companies.preview.site')} />
        ) : null}
      </Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
        {t('companies.preview.inspectedBy')}{' '}
        <b>{memberName ?? t('companies.preview.memberName')}</b>
      </Typography>
    </Paper>
  );
};
